// src/api/campaigns.ts

export type CampaignStatus =
  | 'draft'
  | 'running'
  | 'scheduled'
  | 'paused'
  | 'finished'
  | 'cancelled'
  | string; // keep flexible


export interface CampaignList {
  id: number | null;
  name: string;
}

export interface CampaignSummary {
  id: number;
  name: string;
  subject: string;
  status: CampaignStatus;
  type?: string;
  contentType?: string;
  fromEmail?: string;
  lists: CampaignList[];
  tags: string[];
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string | null;
  sendAt?: string | null;
  toSend?: number;
  sent?: number;
  views?: number;
  clicks?: number;
  adminUrl?: string | null;
  previewUrl?: string | null;
}

export interface CampaignDetail extends CampaignSummary {
  body?: string | null;
  altbody?: string | null;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export async function fetchCampaigns(
  status?: CampaignStatus | 'all'
): Promise<CampaignSummary[]> {
  const params = new URLSearchParams();
  if (status && status !== 'all') {
    params.append('status', status);
  }

  const url = `${API_BASE}/api/campaigns${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch campaigns: ${res.status}`);
  }

  const data = await res.json();
  if (!data.ok) {
    throw new Error(data.error || 'Failed to fetch campaigns.');
  }

  return data.campaigns as CampaignSummary[];
}

export async function fetchCampaignById(
  id: number
): Promise<CampaignDetail> {
  const res = await fetch(`${API_BASE}/api/campaigns/${id}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch campaign ${id}: ${res.status}`);
  }

  const data = await res.json();
  if (!data.ok) {
    throw new Error(data.error || `Failed to fetch campaign ${id}.`);
  }

  return data.campaign as CampaignDetail;
}

export async function sendCampaignNow(id: number): Promise<CampaignSummary> {
  const res = await fetch(`${API_BASE}/api/campaigns/${id}/send`, {
    method: 'POST',
  });

  if (!res.ok) {
    throw new Error(`Failed to send campaign ${id}: ${res.status}`);
  }

  const data = await res.json();
  if (!data.ok) {
    throw new Error(data.error || `Failed to send campaign ${id}.`);
  }

  return data.campaign as CampaignSummary;
}
